import { XClose } from "@untitledui/icons";
import type { ComponentPropsWithoutRef, ReactNode } from "react";
import { useId } from "react";
import { cx } from "@/utils/cx";
import { Button } from "../button/button-untitledui";

/**
 * Altura visual dos campos de filtro (36 px nominais).
 *
 * É a mesma altura do `Button` de `button-untitledui` com `size="sm"`
 * (`py-2` com linha de 20 px), o que mantém campos e ações alinhados na barra.
 * Aplique em `Input` e `DatePicker` por `className`. `Select` e
 * `DateRangePicker` recebem `size="sm"`.
 */
export const FILTER_CONTROL_HEIGHT_CLASS = "h-9";

export type FilterBarProps = Omit<
	ComponentPropsWithoutRef<"fieldset">,
	"aria-label" | "aria-labelledby"
> & {
	/** Nome acessível da região de filtros, por exemplo "Filtros de infrações". */
	label: string;
	/** Campos de filtro. Use `FilterField` para rótulo e largura coerentes. */
	children: ReactNode;
	/**
	 * Ações da barra, como limpar, exportar e criar. Ficam no fim da linha em
	 * telas largas e ocupam a largura toda, empilhadas, em telas pequenas.
	 */
	actions?: ReactNode;
};

/**
 * Contêiner compartilhado de filtros.
 *
 * Reúne a superfície interna usada em Infrações (`rounded-xl`, borda,
 * `p-6`, `gap-4`) e o comportamento responsivo: coluna em telas pequenas, linha
 * com quebra a partir de `sm`, campos alinhados pela base e ações no fim.
 *
 * Só define a composição. Busca, datas, limites de período e o significado da
 * limpeza continuam sob responsabilidade de cada domínio.
 */
export function FilterBar({
	label,
	children,
	actions,
	className,
	...props
}: FilterBarProps) {
	return (
		<fieldset
			aria-label={label}
			className={cx(
				"m-0 mb-6 flex min-w-0 flex-col gap-4 rounded-xl border border-border-secondary bg-primary p-6 sm:flex-row sm:flex-wrap sm:items-end",
				className
			)}
			data-slot="filter-bar"
			{...props}
		>
			{children}
			{actions ? (
				<div
					className="flex w-full flex-col gap-4 *:w-full sm:ml-auto sm:w-auto sm:flex-row sm:items-center sm:*:w-auto"
					data-slot="filter-bar-actions"
				>
					{actions}
				</div>
			) : null}
		</fieldset>
	);
}

export type FilterFieldProps = {
	/** Rótulo do campo. É obrigatório: todo filtro precisa de um nome. */
	label: string;
	/**
	 * `visible` mostra o rótulo acima do controle. `hidden` mantém o nome só
	 * para tecnologias assistivas, para campos cujo placeholder já explica a
	 * função, como a busca.
	 */
	labelVisibility?: "visible" | "hidden";
	/**
	 * Id do controle nativo associado. Sem ele, o campo vira um grupo nomeado
	 * pelo rótulo, adequado a controles compostos (datas, combobox) que já
	 * recebem `aria-label` próprio.
	 */
	htmlFor?: string;
	/** Faz o campo ocupar o espaço restante da linha em telas a partir de `sm`. */
	grow?: boolean;
	className?: string;
	children: ReactNode;
};

const LABEL_VISIBLE_CLASS = "font-medium text-secondary text-sm";

/**
 * Campo de filtro: rótulo e controle com largura mínima comum.
 *
 * O rótulo vem de `label`; o controle interno não perde seu `aria-label`.
 */
export function FilterField({
	label,
	labelVisibility = "visible",
	htmlFor,
	grow = false,
	className,
	children,
}: FilterFieldProps) {
	const labelId = useId();
	const labelClass =
		labelVisibility === "hidden" ? "sr-only" : LABEL_VISIBLE_CLASS;
	const rootClass = cx(
		"flex w-full min-w-0 flex-col gap-1.5 sm:w-auto sm:min-w-[220px]",
		grow && "sm:flex-1",
		className
	);

	if (htmlFor) {
		return (
			<div className={rootClass} data-slot="filter-field">
				<label className={labelClass} htmlFor={htmlFor}>
					{label}
				</label>
				{children}
			</div>
		);
	}

	return (
		<fieldset
			aria-labelledby={labelId}
			className={cx("m-0 border-0 p-0", rootClass)}
			data-slot="filter-field"
		>
			<span className={labelClass} id={labelId}>
				{label}
			</span>
			{children}
		</fieldset>
	);
}

export type FilterClearButtonProps = {
	/** Ação de limpeza. O significado (remover tudo ou restaurar o padrão) é do domínio. */
	onClick: () => void;
	/** Texto do botão. O padrão é "Limpar filtros". */
	label?: string;
	isDisabled?: boolean;
	className?: string;
};

/**
 * Botão de limpeza de filtros.
 *
 * Padroniza apresentação e posição (secundária, `sm`, ícone à esquerda), mas
 * não decide o que "limpar" significa. Em Eventos e Ranking, por exemplo, a
 * ação restaura os filtros iniciais da tela e não remove todas as restrições;
 * quem usa o botão implementa esse comportamento em `onClick`.
 */
export function FilterClearButton({
	onClick,
	label = "Limpar filtros",
	isDisabled,
	className,
}: FilterClearButtonProps) {
	return (
		<Button
			className={className}
			color="tertiary"
			data-slot="filter-clear"
			iconLeading={XClose}
			isDisabled={isDisabled}
			onClick={onClick}
			size="sm"
		>
			{label}
		</Button>
	);
}
